// ── Bio-Quote API client ───────────────────────────────────────────────────────
// Backend: functions/intranet/src/functions/auswertungBioQuote.js
// Quote = Bio-€ / Lebensmittel-€ (netto, 0..1, null ohne Lebensmittel).
// Zusatzprodukte (Non-Food, Pfand, Gebühr, Alkohol) stehen separat und zählen nie in die Quote.

import { useQuery, keepPreviousData } from '@tanstack/react-query'

export interface BioMoney {
  lebensmittel: number
  bio: number
  konventionell: number
  zertifiziert: number
  /** Lebensmittel-€ ganz ohne Bio-Angabe — zählt als "kein Bio", die Quote ist eine Untergrenze. */
  ohne_angabe: number
  non_food: number
  pfand: number
  gebuehr: number
  alkohol: number
  /** non_food + pfand + gebuehr + alkohol */
  zusatz: number
  quote: number | null
}

export interface BioQuoteKostenstelle extends BioMoney {
  /** FM-Schul-ID bzw. 'ks:<id>' für operative Kostenstellen. */
  schoolId: string
  targetKind: 'school' | 'operative'
  name?: string
  category?: string
  firma?: string
}

export interface BioQuoteLieferant {
  supplier_id: string
  name: string
  tage: number
  monate: number
  /** YYYY-MM-DD */
  erster: string
  /** YYYY-MM-DD */
  letzter: string
  netto: number
  /** Rechnet einmal im Monat ab (z. B. ASS) — in der Tagesansicht liegt der ganze Monat auf einem Tag. */
  monatsrechnung: boolean
}

export interface BioQuoteOverview {
  von: string
  bis: string
  gesamt: BioMoney
  kostenstellen: BioQuoteKostenstelle[]
  lieferanten: BioQuoteLieferant[]
  /** Nur mit `stufe`: Zeitreihe aller Kostenstellen (gleiche Form wie der Verlauf). */
  stufe?: BioStufe
  punkte?: BioQuotePunkt[]
  lieferantenJePeriode?: { periode: string; supplier_id: string; netto: number }[]
}

export type BioStufe = 'tag' | 'monat' | 'jahr'
/** '' = beide Firmen */
export type BioFirma = '' | 'GmbH' | 'Schulcatering GmbH'

export interface BioQuotePunkt extends BioMoney {
  /** 'YYYY-MM-DD' | 'YYYY-MM' | 'YYYY' je nach Stufe */
  periode: string
}

export interface BioQuoteVerlauf {
  von: string
  bis: string
  stufe?: BioStufe
  gesamt: BioMoney
  punkte: BioQuotePunkt[]
  lieferantenJePeriode: { periode: string; supplier_id: string; netto: number }[]
  lieferanten: BioQuoteLieferant[]
}

async function getJson<T>(url: string, fallback: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `${fallback} (${res.status})`)
  }
  return res.json()
}

/** Alle Kostenstellen im Zeitraum (bis exklusiv, YYYY-MM-DD); mit `stufe` inkl. Zeitreihe aller. */
export function useBioQuoteOverview(params: { von: string; bis: string; stufe?: BioStufe; firma?: BioFirma }) {
  const qs = new URLSearchParams({ von: params.von, bis: params.bis })
  if (params.stufe) qs.set('stufe', params.stufe)
  if (params.firma) qs.set('firma', params.firma)
  return useQuery({
    queryKey: ['bio-quote-overview', params.von, params.bis, params.stufe ?? '', params.firma ?? ''] as const,
    queryFn: () => getJson<BioQuoteOverview>(`/api/auswertung/bio-quote?${qs.toString()}`, 'Bio-Quote konnte nicht geladen werden'),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

/** Zeitreihe; `ziele` leer/fehlend = alle Kostenstellen. */
export function useBioQuoteVerlauf(params: { von: string; bis: string; stufe: BioStufe; ziele?: string[]; firma?: BioFirma; enabled?: boolean }) {
  const qs = new URLSearchParams({ von: params.von, bis: params.bis, stufe: params.stufe })
  if (params.ziele?.length) qs.set('ziele', params.ziele.join(','))
  if (params.firma) qs.set('firma', params.firma)
  return useQuery({
    queryKey: ['bio-quote-verlauf', params.von, params.bis, params.stufe, params.ziele?.join(',') ?? '', params.firma ?? ''] as const,
    queryFn: () => getJson<BioQuoteVerlauf>(`/api/auswertung/bio-quote/verlauf?${qs.toString()}`, 'Bio-Quote-Verlauf konnte nicht geladen werden'),
    enabled: params.enabled ?? true,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}
